// /app/frontend/src/pages/RoomPage.jsx
import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Flag, Sparkles, RefreshCw, Shield, Award, Heart, Users, Compass, Star } from "lucide-react";
import { toast } from "sonner";
import apiClient from "../api/client";
import {
  createReply,
  createRoomPost,
  getRoom,
  getRoomPin,
  listPostReplies,
  listRoomPosts,
  reportPost,
  reportReply,
} from "../api/rooms";
import { AuthContext } from "../App";
import { useEntitlements } from "../context/EntitlementsContext";
import { trackEvent } from "../api/analytics";
import { goVerify as goVerifyNav } from "../utils/verificationNav";
import { useRoomPromptsToday } from "../hooks/useRoomPromptsToday";
import { useCommunityRhythmToday } from "../hooks/useCommunityRhythmToday";
import RoomPromptBanner from "../components/RoomPromptBanner";
import RoomMemoryBadge from "../components/RoomMemoryBadge";
import NextVisitNudge from "../components/NextVisitNudge";
import { useRecognitionBadges } from "../hooks/useRecognition";
import { useRoomParticipation } from "../hooks/useRoomMemory";

const API = window.location.origin;

// Her Space channel definitions - mirrors backend
const HER_SPACE_CHANNELS = [
  { key: "general", label: "General", icon: Heart },
  { key: "sister_wives", label: "Sister Wives", icon: Users },
  { key: "boundaries", label: "Boundaries", icon: Shield },
  { key: "healing", label: "Healing", icon: Heart },
  { key: "mothers", label: "Mothers", icon: Star },
  { key: "culture", label: "Culture", icon: Sparkles },
  { key: "new_here", label: "New Here", icon: Compass },
];

function timeLabel(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

export default function RoomPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token } = useContext(AuthContext);
  const { hasFeature } = useEntitlements();

  const [room, setRoom] = useState(null);
  const [pin, setPin] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newPost, setNewPost] = useState("");
  const [posting, setPosting] = useState(false);

  // PATCH B: Next-visit nudge state
  const [showExitNudge, setShowExitNudge] = useState(false);

  // Her Space channel state
  const isHerSpacePrivate = slug === "her_space_private";
  const [selectedChannel, setSelectedChannel] = useState(searchParams.get("channel") || "general");
  const [defaultChannelLoaded, setDefaultChannelLoaded] = useState(false);

  // Room prompts and community rhythm
  const { getPromptForRoom, getFullPromptForRoom, hasPromptForRoom } = useRoomPromptsToday();
  const { theme: rhythmTheme, tagline: rhythmTagline, hasTheme: hasRhythmTheme } = useCommunityRhythmToday();
  
  // PATCH B: Room Memory - Track participation
  const { refetch: refetchParticipation } = useRoomParticipation(slug);
  
  // PATCH B: Recognition badges
  const { badges: recognitionBadges, refetch: refetchBadges } = useRecognitionBadges();
  
  // Map slug to room_key for prompts (normalize legacy slugs to canonical)
  const roomKeyMap = {
    'lounge': 'lounge',
    'her-space': 'her_space',
    'her_space': 'her_space',
    'herspace': 'her_space',
    'meditation': 'meditation',
    'meditation-corner': 'meditation',
    'universe': 'the_universe',
    'the-universe': 'the_universe',
    'the_universe': 'the_universe',
    'mens-work': 'mens_work',
    'mens_work': 'mens_work',
    'menswork': 'mens_work',
    'poly-curious': 'poly_curious',
    'poly_curious': 'poly_curious',
  };
  // Normalize: convert hyphens to underscores, then check map
  const normalizedSlug = slug?.toLowerCase().replace(/-/g, '_');
  const roomKey = roomKeyMap[slug] || roomKeyMap[normalizedSlug] || normalizedSlug || slug;
  const todaysPrompt = getPromptForRoom(roomKey);
  const fullPrompt = getFullPromptForRoom(roomKey); // Full object with source, cycle_day, cycle_length

  // Starter Voice: unverified members can make 1 original post/day and reply freely.
  // Verified members unlock unlimited posting and the rest of the trust features.
  const canSpeak = hasFeature('can_speak_in_rooms');
  
  // Use canonical verification navigation
  const goVerify = (source = 'banner') => {
    trackEvent("room_speak_attempted_unverified", { room_slug: slug, source });
    goVerifyNav(navigate, location, null, { source: `room_${source}`, isLoggedIn: !!user });
  };

  // Her Space: Load user's default channel preference if no channel in URL
  useEffect(() => {
    const loadDefaultChannel = async () => {
      if (!isHerSpacePrivate || !token || defaultChannelLoaded) return;
      
      const urlChannel = searchParams.get("channel");
      if (urlChannel) {
        setSelectedChannel(urlChannel);
        setDefaultChannelLoaded(true);
        return;
      }
      
      try {
        const res = await apiClient.get(`/rooms/her-space/preference`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const defaultCh = res.data?.default_channel || "general";
        setSelectedChannel(defaultCh);
        // Update URL to reflect default channel
        setSearchParams({ channel: defaultCh }, { replace: true });
      } catch (err) {
        console.log("No saved channel preference, using general");
      } finally {
        setDefaultChannelLoaded(true);
      }
    };
    
    loadDefaultChannel();
  }, [isHerSpacePrivate, token, searchParams, defaultChannelLoaded, setSearchParams]);

  // Frontend gender gate for Her Space rooms — defense in depth.
  // If the user is not a woman, bounce them to /her-space (which shows the
  // proper "not allowed" lock screen) before any room data loads.
  useEffect(() => {
    const slugLower = (slug || "").toLowerCase();
    const isHerSpaceSlug = slugLower === "her_space" || slugLower === "her-space" || slugLower === "herspace" || slugLower === "her_space_private";
    if (!isHerSpaceSlug || !token) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/her-space/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled && res.data && res.data.allowed === false) {
          navigate("/her-space", { replace: true });
        }
      } catch (err) {
        if (!cancelled && err?.response?.status === 403) {
          navigate("/her-space", { replace: true });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [slug, token, navigate]);

  // Her Space: Handle channel change
  const handleChannelChange = useCallback((channelKey) => {
    setSelectedChannel(channelKey);
    setSearchParams({ channel: channelKey }, { replace: true });
  }, [setSearchParams]);

  // Refresh posts (with channel filter for Her Space)
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const roomPromise = getRoom(slug);
      const pinPromise = getRoomPin(slug);
      
      // For Her Space, pass channel filter to API
      let postsPromise;
      if (isHerSpacePrivate && selectedChannel) {
        postsPromise = apiClient.get(`/rooms/${slug}/posts?channel=${selectedChannel}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).then(res => res.data).catch(() => []);
      } else {
        postsPromise = listRoomPosts(slug);
      }
      
      const [r, p, list] = await Promise.all([roomPromise, pinPromise, postsPromise]);
      setRoom(r);
      setPin(p);
      setPosts(Array.isArray(list) ? list : []);
    } finally {
      setLoading(false);
    }
  }, [slug, isHerSpacePrivate, selectedChannel, token]);

  useEffect(() => {
    refresh();
  }, [slug, selectedChannel]);

  const canPost = useMemo(() => newPost.trim().length > 0 && !posting, [newPost, posting]);

  // PATCH B: Prompt Completion Echo - Show toast after posting
  const submitPost = async () => {
    if (!canPost) return;
    setPosting(true);
    try {
      // For Her Space, include the channel in the post
      const channel = isHerSpacePrivate ? selectedChannel : null;
      await createRoomPost(slug, newPost.trim(), channel);
      setNewPost("");
      
      // PATCH B: Prompt Completion Echo - Auto-dismissing acknowledgment
      const isPromptResponse = todaysPrompt && newPost.toLowerCase().includes(todaysPrompt.toLowerCase().slice(0, 20));
      if (isPromptResponse) {
        toast.success("Your voice matters. Thanks for sharing.", {
          duration: 3000,
          icon: "💭",
        });
      } else {
        toast.success("Shared with the room", {
          duration: 2000,
        });
      }
      
      // Refetch participation and badges after posting
      refetchParticipation();
      refetchBadges();
      
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not post right now.";
      toast.error(detail);
      if (err?.response?.status === 403 && !canSpeak) {
        trackEvent("verify_cta_shown_after_starter_voice_limit", { room_slug: slug });
      }
    } finally {
      setPosting(false);
    }
  };

  // Context-aware back navigation
  // Her Space returns to hub; other rooms return to global rooms list
  const fromParam = searchParams.get("from");
  
  const getBackDestination = useCallback(() => {
    // Her Space private room: return to hub (explicit or fallback)
    if (slug === "her_space_private") {
      return "/her-space";
    }
    // All other rooms: return to global rooms list
    return "/rooms";
  }, [slug]);
  
  const getBackLabel = useCallback(() => {
    if (slug === "her_space_private") {
      return "Back to Her Space";
    }
    return "Back";
  }, [slug]);

  // PATCH B: Next-Visit Nudge - Show when navigating away
  const handleBackClick = useCallback(() => {
    const destination = getBackDestination();
    
    // Show the nudge before navigating (for rooms with rhythm theme)
    if (hasRhythmTheme && slug !== "her_space_private") {
      setShowExitNudge(true);
      setTimeout(() => {
        navigate(destination);
      }, 300);
    } else {
      navigate(destination);
    }
  }, [navigate, hasRhythmTheme, getBackDestination, slug]);

  return (
    <div className="min-h-screen surface-page">
      {/* PATCH B: Next-Visit Nudge */}
      <NextVisitNudge 
        show={showExitNudge} 
        onDismiss={() => setShowExitNudge(false)} 
      />
      
      <div className="mx-auto w-full max-w-3xl px-4 py-6 pb-24">
        {/* Header */}
        <header className="mb-6">
          <button
            type="button"
            className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition mb-4"
            onClick={handleBackClick}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">{getBackLabel()}</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {room?.name || "Room"}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {room?.description || ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* PATCH B: Room Memory Badge */}
              <RoomMemoryBadge roomKey={slug} />
              <button
                type="button"
                className="rounded-xl p-2.5 hover:bg-gray-100 dark:hover:bg-slate-800 transition"
                onClick={refresh}
                disabled={loading}
                aria-label="Refresh"
              >
                <RefreshCw className={`w-5 h-5 text-gray-500 dark:text-gray-400 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>
        </header>

        {/* Her Space Channel Tabs */}
        {isHerSpacePrivate && (
          <section className="mb-6" data-testid="her-space-channel-tabs">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {HER_SPACE_CHANNELS.map((ch) => {
                const IconComponent = ch.icon;
                const isActive = selectedChannel === ch.key;
                return (
                  <button
                    key={ch.key}
                    type="button"
                    onClick={() => handleChannelChange(ch.key)}
                    className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      isActive
                        ? "bg-rose-500 text-white shadow-sm"
                        : "bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-700"
                    }`}
                    data-testid={`channel-tab-${ch.key}`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{ch.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Showing posts in: <span className="font-medium text-rose-600 dark:text-rose-400">{HER_SPACE_CHANNELS.find(c => c.key === selectedChannel)?.label || selectedChannel}</span>
            </p>
          </section>
        )}

        {/* PATCH B: Recognition Badges (if earned) */}
        {recognitionBadges && recognitionBadges.length > 0 && (
          <section className="mb-4">
            <div className="flex flex-wrap items-center gap-2">
              {recognitionBadges.map((badge) => (
                <span
                  key={badge.key}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
                  title={badge.description}
                >
                  <Award className="w-3 h-3" />
                  <span>{badge.emoji}</span>
                  <span>{badge.label}</span>
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Daily Topic + Community Theme */}
        {(todaysPrompt || hasRhythmTheme) && (
          <section className="mb-6">
            <RoomPromptBanner
              promptText={todaysPrompt}
              theme={rhythmTheme}
              tagline={rhythmTagline}
              showTheme={hasRhythmTheme}
              source={fullPrompt?.source}
              cycleDay={fullPrompt?.cycle_day}
              cycleLength={fullPrompt?.cycle_length}
            />
          </section>
        )}

        {/* Listen-only banner for unverified users */}
        {!canSpeak && (
          <section className="mb-6">
            <div className="surface-card rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-slate-800 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 block">
                    Starter Voice active
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    You can reply freely and start 1 post per day. Verify for unlimited posting.
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="w-full rounded-xl bg-gray-900 dark:bg-slate-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 dark:hover:bg-slate-600 transition"
                onClick={goVerify}
              >
                Verify for unlimited posting
              </button>
            </div>
          </section>
        )}

        {/* Post Composer */}
        <section className="mb-6">
          <div className="surface-card rounded-2xl p-5 shadow-sm">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Share a thought
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Keep it respectful. Report anything off.
            </div>
            <textarea
              className="h-28 w-full resize-none rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 p-4 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-gray-300 dark:focus:border-slate-500 focus:ring-2 focus:ring-rose-500/20 transition"
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              placeholder={canSpeak ? "Ask a question, share a thought, or respond to the daily topic…" : "Starter Voice: start 1 post today…"}
            />
            <div className="mt-4 flex items-center justify-end">
              <button
                className="rounded-xl px-6 py-2.5 text-sm font-medium text-white transition bg-rose-500 hover:bg-rose-600 shadow-sm"
                onClick={submitPost}
                aria-disabled={!canPost}
              >
                {posting ? "Posting…" : "Post"}
              </button>
            </div>
          </div>
        </section>

        {/* Posts List */}
        <section className="space-y-4">
          {loading && posts.length === 0 ? (
            <div className="surface-card rounded-2xl p-8 text-center">
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading posts…</div>
            </div>
          ) : posts.length === 0 ? (
            <div className="surface-card rounded-2xl p-8 text-center">
              <MessageCircle className="w-10 h-10 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">No posts yet</p>
              <p className="text-xs text-gray-500 dark:text-gray-500">Be the first to share something.</p>
            </div>
          ) : (
            posts.map((p) => (
              <RoomPostCard
                key={p.id}
                post={p}
                user={user}
                canSpeak={canSpeak}
                onVerify={goVerify}
                onReport={async () => {
                  await reportPost(p.id, "inappropriate");
                  alert("Post reported. Thank you.");
                }}
                onRefresh={refresh}
              />
            ))
          )}
        </section>
      </div>
    </div>
  );
}

function RoomPostCard({ post, user, canSpeak, onVerify, onReport, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const loadReplies = async () => {
    setLoading(true);
    try {
      const r = await listPostReplies(post.id);
      setReplies(Array.isArray(r) ? r : []);
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await loadReplies();
  };

  const canReply = text.trim().length > 0 && !sending;

  const submitReply = async () => {
    if (!canReply) return;
    setSending(true);
    try {
      await createReply(post.id, text.trim());
      setText("");
      await loadReplies();
      await onRefresh?.();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="surface-card rounded-2xl p-5 shadow-sm">
      {/* Post Content */}
      <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
        {post.content}
      </div>
      
      {/* Post Footer */}
      <div className="mt-4 flex items-center justify-between pt-3 border-t border-gray-100 dark:border-slate-700/50">
        <span className="text-xs text-gray-400 dark:text-gray-500">{timeLabel(post.created_at)}</span>
        <div className="flex items-center gap-1">
          <button
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition"
            onClick={toggle}
          >
            <MessageCircle className="w-3.5 h-3.5" />
            <span>{open ? "Hide" : `${post.reply_count || 0} replies`}</span>
          </button>
          <button
            className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-red-500 transition"
            onClick={onReport}
            aria-label="Report post"
          >
            <Flag className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Replies Section */}
      {open && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700/50">
          {loading ? (
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">Loading replies…</div>
          ) : replies.length === 0 ? (
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">No replies yet. Start the conversation.</div>
          ) : (
            <div className="space-y-3 mb-4">
              {replies.map((r) => (
                <div key={r.id} className="rounded-xl bg-gray-50 dark:bg-slate-800/50 p-4">
                  <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {r.content}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-gray-400 dark:text-gray-500">{timeLabel(r.created_at)}</span>
                    <button
                      className="rounded-lg p-1.5 text-xs text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-red-500 transition"
                      onClick={async () => {
                        await reportReply(r.id, "inappropriate");
                        alert("Reply reported. Thank you.");
                        await loadReplies();
                      }}
                      aria-label="Report reply"
                    >
                      <Flag className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Reply Composer */}
          <div className="rounded-xl bg-gray-50 dark:bg-slate-800/50 p-4">
            <textarea
              className="h-20 w-full resize-none rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 p-3 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-gray-300 dark:focus:border-slate-500 focus:ring-2 focus:ring-rose-500/20 transition"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write a thoughtful reply…"
            />
            <div className="mt-3 flex justify-end">
              <button
                className="rounded-lg px-4 py-2 text-xs font-medium text-white transition bg-gray-900 dark:bg-slate-600 hover:bg-gray-800 dark:hover:bg-slate-500"
                onClick={submitReply}
                aria-disabled={!canReply}
              >
                {sending ? "Sending…" : "Reply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
